import { cn } from "@-/com.npmjs.cn";
import {
  Indicator as ProgressIndicator,
  Root,
  type RootProps,
  type RootRef,
} from "@rn-primitives/progress";
import { Platform, View } from "@-/com.npmjs.react-native";
import {
  useAnimatedStyle,
  withTiming,
  createAnimatedComponent,
} from "@-/com.npmjs.react-native-reanimated";

const AnimatedProgressIndicator = createAnimatedComponent(ProgressIndicator);

const Indicator = Platform.OS === "web" ? WebIndicator : NativeIndicator;

export function Progress({
  className,
  indicatorClassName,
  indicatorColor,
  value,
  ...props
}: RootProps &
  React.RefAttributes<RootRef> & {
    indicatorClassName?: string;
    /** A fill color computed at runtime; it eases along with the fill. Overrides a `bg-*` class. */
    indicatorColor?: string;
  }) {
  return (
    <Root
      className={cn("relative h-2 w-full overflow-hidden rounded-full bg-primary/20", className)}
      value={value}
      {...props}
    >
      <Indicator className={indicatorClassName} color={indicatorColor} value={value} />
    </Root>
  );
}

interface IndicatorProps {
  className?: string;
  color: string | undefined;
  value: number | undefined | null;
}

function WebIndicator({ className, color, value = 0 }: IndicatorProps) {
  return (
    <View
      className={cn("size-full flex-1 bg-primary transition-all", className)}
      style={{ backgroundColor: color, transform: `translateX(-${100 - (value ?? 0)}%)` }}
    >
      <ProgressIndicator
        className={cn("size-full transition-all", className)}
        style={{ backgroundColor: color }}
      />
    </View>
  );
}

function NativeIndicator({ className, color, value = 0 }: IndicatorProps) {
  const clampedValue = Math.max(0, Math.min(100, value ?? 0));

  // Slide a full-width fill under the track's rounded clip. Updating its width
  // would lay out the progress bar on every frame; a translation leaves its
  // geometry and rounded trailing edge intact. Percentage translation also
  // follows track resizes without a JS onLayout measurement.
  const animatedStyle = useAnimatedStyle(() => ({
    ...(color === undefined ? {} : { backgroundColor: withTiming(color, { duration: 350 }) }),
    transform: [{ translateX: withTiming(`${clampedValue - 100}%`, { duration: 350 }) }],
  }));

  return (
    <AnimatedProgressIndicator
      className={cn("size-full bg-foreground", className)}
      style={animatedStyle}
      testID="progress-indicator"
    />
  );
}
